type LogLevel = 'log' | 'warn' | 'error' | 'info' | 'debug';

export interface BufferedLogEntry {
  ts: string;
  level: LogLevel;
  args: string[];
}

const MAX_ENTRIES = 100;
const buffer: BufferedLogEntry[] = [];
let installed = false;

const safeStringify = (value: unknown): string => {
  if (value instanceof Error) {
    return `${value.name}: ${value.message}\n${value.stack ?? ''}`.trim();
  }
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || value === null) {
    return String(value);
  }
  if (typeof value === 'undefined') return 'undefined';
  try {
    return JSON.stringify(value);
  } catch {
    return Object.prototype.toString.call(value);
  }
};

const push = (level: LogLevel, args: unknown[]) => {
  if (buffer.length >= MAX_ENTRIES) buffer.shift();
  buffer.push({
    ts: new Date().toISOString(),
    level,
    args: args.map(safeStringify).map((s) => (s.length > 500 ? `${s.slice(0, 500)}…` : s)),
  });
};

export const installConsoleLogBuffer = (): void => {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const levels: LogLevel[] = ['log', 'warn', 'error', 'info', 'debug'];
  for (const level of levels) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      push(level, args);
      original(...args);
    };
  }
  window.addEventListener('error', (e) => {
    push('error', [`window.onerror: ${e.message} @ ${e.filename}:${e.lineno}:${e.colno}`]);
  });
  window.addEventListener('unhandledrejection', (e) => {
    push('error', [`unhandledrejection: ${safeStringify(e.reason)}`]);
  });
};

export const getBufferedLogs = (): BufferedLogEntry[] => buffer.slice();
